import {
  useAppcues,
  useCallback,
  useEffect,
  useMutations,
  useQueries,
  useReload,
  useSelector,
  useState,
} from 'src/hooks'

import {
  EditorElement,
  EditorFullBleed,
  EditorImage,
  EditorPanel,
  factoryFullBleeds,
  Position,
} from 'src/editor/EditorCard'

import { TextEditorState, TextSelection } from 'src/editor/text/types'

import { cardUpdate, createCleanBackPanel } from 'src/editor/CardUpdate'

import {
  CardFragment,
  CardType,
  getCard,
  getFonts,
  ImageFragment,
} from 'src/graphql'

import { Props, Step, Steps } from './types'
import * as Utils from './utils'

const idle = (fullBleedIndex: number): Step => ({
  type: Steps.Idle,
  fullBleedIndex,
  panelIndex: 0,
  elementIndex: undefined,
  textSelection: undefined,
})

type OptionalPanel<T extends Step> = T['elementIndex'] extends number
  ? EditorPanel
  : undefined | EditorPanel

type OptionalElement<T extends Step> = T['elementIndex'] extends number
  ? T['panelIndex'] extends number
    ? EditorElement
    : undefined | EditorElement
  : undefined | EditorElement

const useApi = (props: Props) => {
  const { cardId, onAfterSave, onClose } = props

  const cardQuery = getCard({ id: cardId })
  const [card, fonts] = useQueries(cardQuery, getFonts())

  const mutations = useMutations()
  const reloadQuery = useReload()
  const [fullBleeds, setFullBleeds] = useState(factoryFullBleeds(card))
  useEffect(() => setFullBleeds(factoryFullBleeds(card)), [card])
  const { isMobile } = useSelector(state => state.window)
  const [isCreatingBackPanel, setIsCreatingBackPanel] = useState(false)
  const [view, setView] = useState<'fullbleed' | 'panel'>(
    isMobile ? 'panel' : 'fullbleed',
  )
  const [panelView, setPanelView] = useState<'fullbleed' | 'panel'>(
    isMobile ? 'panel' : 'fullbleed',
  )
  const [isSaving, setIsSaving] = useState(false)

  const [step, setStep] = useState<Step>(idle(0))
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>()
  const resetStep = () => setStep(idle(step.fullBleedIndex))
  const { fullBleedIndex, panelIndex } = step

  const updateCard = async (
    card: CardFragment,
    fullBleeds: EditorFullBleed[],
  ) => {
    await mutations.updateCard({ card: cardUpdate(card, fullBleeds) })
    reloadQuery(cardQuery)
  }

  const resolveLocation = (selectedCardType: CardType) => {
    if (fullBleedIndex === 0) {
      return 'Front'
    }
    if (fullBleedIndex) {
      switch (selectedCardType) {
        case CardType.POSTCARD:
        case CardType.FLATCARD:
          if (fullBleedIndex === 1) {
            return 'Back'
          }
          break
        case CardType.TWO_PANEL:
        case CardType.TWO_PANEL_BIG:
          if (fullBleedIndex === 1 && panelView === 'fullbleed') {
            return 'Inside'
          } else if (fullBleedIndex === 1 && panelView === 'panel') {
            return panelIndex === 0
              ? 'Inside Left'
              : panelIndex === 1
              ? 'Inside Right'
              : 'Inside'
          } else if (fullBleedIndex === 2) {
            return 'Back'
          }
          break
        case CardType.THREE_PANEL:
          if (fullBleedIndex === 1 && panelView === 'fullbleed') {
            return 'Inside'
          } else if (fullBleedIndex === 1 && panelView === 'panel') {
            return panelIndex === 0
              ? 'Inside Left'
              : panelIndex === 1
              ? 'Inside Middle'
              : panelIndex === 2
              ? 'Inside Right'
              : 'Inside'
          } else if (fullBleedIndex === 2) {
            return 'Outside Flap'
          } else if (fullBleedIndex === 3) {
            return 'Back'
          }
          break
        default:
          return null
      }
    }
    return
  }

  const resolveNavigation = (
    currentLocation: string | undefined | null,
    selectedCardType: CardType,
    isPrev: boolean,
  ) => {
    switch (selectedCardType) {
      case CardType.POSTCARD:
      case CardType.FLATCARD:
        if (currentLocation === 'Front') {
          return isPrev ? null : 'Back'
        }
        if (currentLocation === 'Back') {
          return isPrev ? 'Front' : null
        }
        break
      case CardType.TWO_PANEL:
      case CardType.TWO_PANEL_BIG:
        if (panelView === 'fullbleed') {
          if (currentLocation === 'Front') {
            return isPrev ? null : 'Inside'
          } else if (currentLocation === 'Inside') {
            return isPrev ? 'Front' : 'Back'
          } else if (currentLocation === 'Back') {
            return isPrev ? 'Inside' : null
          }
        } else if (panelView === 'panel') {
          if (currentLocation === 'Front') {
            return isPrev ? null : 'Inside Left'
          } else if (currentLocation === 'Inside Left') {
            return isPrev ? 'Front' : 'Inside Right'
          } else if (currentLocation === 'Inside Right') {
            return isPrev ? 'Inside Left' : 'Back'
          } else if (currentLocation === 'Back') {
            return isPrev ? 'Inside Right' : null
          }
        }
        break
      case CardType.THREE_PANEL:
        if (panelView === 'fullbleed') {
          if (currentLocation === 'Front') {
            return isPrev ? null : 'Inside'
          } else if (currentLocation === 'Inside') {
            return isPrev ? 'Front' : 'Outside Flap'
          } else if (currentLocation === 'Outside Flap') {
            return isPrev ? 'Inside' : 'Back'
          } else if (currentLocation === 'Back') {
            return isPrev ? 'Outside Flap' : null
          }
        } else if (panelView === 'panel') {
          if (currentLocation === 'Front') {
            return isPrev ? null : 'Inside Left'
          } else if (currentLocation === 'Inside Left') {
            return isPrev ? 'Front' : 'Inside Middle'
          } else if (currentLocation === 'Inside Middle') {
            return isPrev ? 'Inside Left' : 'Inside Right'
          } else if (currentLocation === 'Inside Right') {
            return isPrev ? 'Inside Middle' : 'Outside Flap'
          } else if (currentLocation === 'Outside Flap') {
            return isPrev ? 'Inside Right' : 'Back'
          } else if (currentLocation === 'Back') {
            return isPrev ? 'Outside Flap' : null
          }
        }
        break
      default:
        return null
    }
    return
  }

  const getTourId = () => {
    if (fullBleedIndex === 0) {
      return '-L9v2SyS08C2D8zfOr9i'
    } else if (step.type === Steps.EditLayout) {
      return '-L8s7aWp_fe9k3CzqMzg'
    } else if (step.type === Steps.EditText) {
      return '-L9qbIuOIV8A8AP_KJAw'
    } else if (step.type === Steps.Idle) {
      return '-LBvgup5KcAEbdohEbSZ'
    } else if (step.type === Steps.EditPicture) {
      return '-L9uqmNyXhnpOmnHswTV'
    } else {
      return undefined
    }
  }

  useAppcues(getTourId())

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await updateCard(card, fullBleeds)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveAndSend = async () => {
    setIsSaving(true)
    try {
      await updateCard(card, fullBleeds)
      await onAfterSave()
    } catch (error) {
      console.error(error.toString())
    } finally {
      setIsSaving(false)
    }
  }

  const updateFullBleed = (
    fullBleedIndex: number,
    update: (fullBleed: EditorFullBleed) => EditorFullBleed,
  ) => setFullBleeds(Utils.updateFullBleed(fullBleeds, fullBleedIndex, update))

  const updatePanel = useCallback(
    (
      fullBleedIndex: number,
      panelIndex: number,
      update: (panel: EditorPanel) => EditorPanel,
    ) => {
      setFullBleeds(
        Utils.updatePanel(fullBleeds, fullBleedIndex, panelIndex, update),
      )
    },
    [setFullBleeds, fullBleeds],
  )

  const editLayout = (fullBleedIndex: number, panelIndex: number) =>
    setStep({ type: Steps.EditLayout, fullBleedIndex, panelIndex })

  const editPicture = (
    fullBleedIndex: number,
    panelIndex: number,
    elementIndex: number,
    source: 'own' | 'social',
  ) =>
    setStep({
      type: Steps.EditPicture,
      fullBleedIndex,
      panelIndex,
      elementIndex,
      source,
    })

  const editBackgroundColor = (fullBleedIndex: number, panelIndex: number) =>
    setStep({ type: Steps.EditBackgroundColor, fullBleedIndex, panelIndex })

  const editBackgroundPicture = (
    fullBleedIndex: number,
    source: 'own' | 'social',
  ) =>
    setStep({
      type: Steps.EditBackgroundPicture,
      fullBleedIndex,
      source,
    })

  const removePicture = (
    fullBleedIndex: number,
    panelIndex: number | undefined,
    elementIndex: number,
  ) =>
    setFullBleeds(
      Utils.updateElementImage(
        fullBleeds,
        fullBleedIndex,
        panelIndex,
        elementIndex,
        () => ({
          image: null,
          position: { x: 0.5, y: 0.5 },
          scale: 1,
          filter: null,
        }),
      ),
    )

  const updateElement = (
    fullBleedIndex: number,
    panelIndex: number,
    elementIndex: number,
    update: (element: EditorElement, panel?: EditorPanel) => EditorElement,
  ) =>
    setFullBleeds(
      Utils.updateElement(
        fullBleeds,
        fullBleedIndex,
        panelIndex,
        elementIndex,
        update,
      ),
    )

  const removeBackgroundPicture = (fullBleedIndex: number) =>
    updateFullBleed(fullBleedIndex, fullBleed => ({
      ...fullBleed,
      backgroundElement: fullBleed.backgroundElement && {
        ...fullBleed.backgroundElement,
        image: {
          image: null,
          position: { x: 0.5, y: 0.5 },
          scale: 1,
          filter: null,
        },
      },
    }))

  const setElementImage = (
    fullBleedIndex: number,
    panelIndex: number | undefined,
    elementIndex: number,
    image: ImageFragment,
  ) =>
    setFullBleeds(
      Utils.updateElementImage(
        fullBleeds,
        fullBleedIndex,
        panelIndex,
        elementIndex,
        () => ({
          image: {
            __typename: 'Image',
            id: image.id,
            url: decodeURI(image.url),
            smallThumb: decodeURI(image.smallThumb || image.url),
            width: image.width,
            height: image.height,
          },
          position: { x: 0.5, y: 0.5 },
          scale: 1,
          filter: null,
        }),
      ),
    )

  const updateElementImage = (
    fullBleedIndex: number,
    panelIndex: number | undefined,
    elementIndex: number,
    update: (image: EditorImage) => EditorImage,
  ) =>
    setFullBleeds(
      Utils.updateElementImage(
        fullBleeds,
        fullBleedIndex,
        panelIndex,
        elementIndex,
        update,
      ),
    )

  const backFullBleedPanel = card ? fullBleeds[fullBleeds.length - 1] : null

  const updateLayout = async (selectedLayoutId: string) => {
    if (!backFullBleedPanel) return

    try {
      await mutations.createOrUpdateLayout({
        panel: createCleanBackPanel(backFullBleedPanel),
        layoutId: selectedLayoutId,
      })
    } catch (error) {
      console.error(error.toString())
    }
  }

  const selectedFullBleed = fullBleeds[step.fullBleedIndex]

  const selectPanel = (fullBleedIndex: number, panelIndex: number) =>
    setStep({
      type: Steps.Idle,
      fullBleedIndex,
      panelIndex,
      elementIndex: undefined,
      textSelection: undefined,
    })

  const selectFullBleed = (fullBleedIndex: number) =>
    setStep({
      type: Steps.Idle,
      fullBleedIndex,
      panelIndex: 0,
      elementIndex: undefined,
      textSelection: undefined,
    })

  const updateText = (
    fullBleedIndex: number,
    panelIndex: number,
    elementIndex: number,
    textSelection: TextSelection,
    update: (text: TextEditorState) => TextEditorState,
  ) => {
    updateElement(fullBleedIndex, panelIndex, elementIndex, element => {
      if (element.text) {
        const nextState = update({
          text: element.text,
          selection: textSelection,
        })
        selectText(
          fullBleedIndex,
          panelIndex,
          elementIndex,
          nextState.selection,
        )

        return {
          ...element,
          text: nextState.text,
        }
      }

      return element
    })
  }

  const selectText = (
    fullBleedIndex: number,
    panelIndex: number,
    elementIndex: number,
    textSelection: TextSelection,
  ) =>
    setStep({
      type: Steps.EditText,
      fullBleedIndex,
      panelIndex,
      elementIndex,
      textSelection,
    })

  const getSelectedElement = <T extends Step>(step: T): OptionalElement<T> => {
    if (
      typeof step.panelIndex === 'number' &&
      typeof step.elementIndex === 'number'
    ) {
      return fullBleeds[step.fullBleedIndex].panels[step.panelIndex].elements[
        step.elementIndex
      ] as OptionalElement<T>
    }
    return undefined as OptionalElement<T>
  }

  const getSelectedPanel = <T extends Step>(step: T): OptionalPanel<T> => {
    if (typeof step.panelIndex === 'number') {
      return fullBleeds[step.fullBleedIndex].panels[
        step.panelIndex
      ] as OptionalPanel<T>
    }
    return undefined as OptionalPanel<T>
  }

  const getCorrectedImagePosition = (
    scale: number,
    position: Position,
  ): Position => {
    // when the scale changes to 1 from anything less than 1
    // it looks like it is fine on the editor, but it
    // is actually positioned wrong, this fix below forces it to center
    // if the user likes this position, it will be fine when
    // flattened, if they want to manually move it, it will still
    // be updated as usual
    return scale >= 0.96 && scale <= 1.04 ? { x: 0.5, y: 0.5 } : position
  }

  return {
    card,
    fullBleeds,
    step,
    setStep,
    setFullBleeds,
    fonts,
    view,
    setView,
    updatePanel,
    selectedLayoutId,
    setSelectedLayoutId,
    isCreatingBackPanel,
    setIsCreatingBackPanel,
    editLayout,
    editBackgroundColor,
    removePicture,
    removeBackgroundPicture,
    selectedFullBleed,
    updateElementImage,
    updateFullBleed,
    editPicture,
    editBackgroundPicture,
    setElementImage,
    isSaving,
    resolveLocation,
    resolveNavigation,
    setPanelView,
    updateLayout,
    handleSave,
    handleSaveAndSend,
    isMobile,
    panelView,
    onClose,
    selectPanel,
    selectFullBleed,
    selectText,
    updateElement,
    updateText,
    resetStep,
    updateCard,
    getSelectedElement,
    getSelectedPanel,
    getCorrectedImagePosition,
  }
}

export default useApi

export type Api = ReturnType<typeof useApi>
